import React from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import App from './App';
import { AuthProvider } from './contexts/AuthContext';
// Initialize Bugsnag early - must be imported before any other code that might throw
import bugsnagClient from './config/bugsnag';
import BugsnagPerformance from '@bugsnag/browser-performance';
import { installHttpAuthHandlers } from './auth/installHttpAuthHandlers';

// Install global 401 handling before any component can issue a request. Without this a 401
// from a data call is silently swallowed by the calling component's catch block, leaving the
// user on a spinner instead of being sent to /login.
installHttpAuthHandlers();

// Initialize Bugsnag Performance Monitoring
// Must be initialized early and linked to error monitoring SDK
// CRA NOTE: REACT_APP_* variables are read at build time only.
// After changing env values, you must rebuild the frontend bundle.
const BUGSNAG_API_KEY = process.env.REACT_APP_BUGSNAG_API_KEY || '';

// Use the same release stage logic as bugsnag.ts
const getReleaseStage = (): string => {
  const env = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const normalizedEnv = env.toLowerCase().trim();
  
  const envMap: Record<string, string> = {
    'dev': 'development',
    'prod': 'production',
    'test': 'testing',
    'stage': 'staging',
  };
  
  const mappedEnv = envMap[normalizedEnv];
  if (mappedEnv) {
    return mappedEnv;
  }
  
  const validEnvironments = ['development', 'staging', 'training', 'testing', 'production'];
  if (validEnvironments.includes(normalizedEnv)) {
    return normalizedEnv;
  }
  
  return 'development';
};

const releaseStage = getReleaseStage();
const enabledReleaseStages = ['development', 'staging', 'training', 'production', 'testing'];
// Use git commit for versioning to align errors and performance traces.
const appVersion = process.env.REACT_APP_GITHUB_COMMIT || process.env.REACT_APP_VERSION || 'unknown';

// Calculate sampling probability based on environment to manage Bugsnag quota
// Lower sampling rates reduce quota usage while maintaining useful traces
const getSamplingProbability = (): number => {
  // Development/Testing: 10% sampling to save quota during development
  if (releaseStage === 'development' || releaseStage === 'testing') {
    return 0.1;
  }
  
  // Staging/Training: 20% sampling for moderate visibility
  if (releaseStage === 'staging' || releaseStage === 'training') {
    return 0.2;
  }
  
  // Production: 5% sampling for high traffic (adjust based on your traffic volume)
  if (releaseStage === 'production') {
    return 0.05;
  }
  
  // Fallback: 10% sampling
  return 0.1;
};

const samplingProbability = getSamplingProbability();

// Initialize Bugsnag Performance with error handling
if (BUGSNAG_API_KEY) {
  try {
    BugsnagPerformance.start({
      apiKey: BUGSNAG_API_KEY,
      serviceName: "qp_plan_demo",
      releaseStage: releaseStage,
      enabledReleaseStages: enabledReleaseStages,
      appVersion,
      bugsnag: bugsnagClient, // Link to error monitoring SDK - this is crucial for proper integration
      samplingProbability: samplingProbability, // Environment-based sampling to manage quota
      autoInstrumentNetworkRequests: true,  // Track all network requests and automatically inject W3C trace context headers (traceparent, tracestate)
      autoInstrumentFullPageLoads: true,          // Track page load performance
      autoInstrumentRouteChanges: true,     // Track route changes in SPAs
      
      // ✅ Trace propagation via networkRequestCallback (standard Bugsnag approach)
      // Conditionally enable trace propagation for API requests to your backend
      networkRequestCallback: (info) => {
        const url = info.url || '';
        
        // Define URL patterns where trace context should be propagated to your backend
        const tracePropagationPatterns = [
          // Relative paths - matches /api/* in all environments (development, test, production)
          /^\/api(\/.*)?$/,
          
          // Local development - backend directly (http://localhost:5000/api/*)
          /^http:\/\/localhost:5000\/api(\/.*)?$/,
          
          // Local development - proxied through frontend (http://localhost:3000/api/*)
          /^http:\/\/localhost:3000\/api(\/.*)?$/,
          
          // Test environment (https://test.wrap.dawsonroadmaintenance.ca/api/*)
          /^https:\/\/(www\.)?test\.wrap\.dawsonroadmaintenance\.ca\/api(\/.*)?$/,
          
          // Production environment (https://wrap.dawsonroadmaintenance.ca/api/*)
          /^https:\/\/(www\.)?wrap\.dawsonroadmaintenance\.ca\/api(\/.*)?$/,
        ];
        
        // Check if URL matches any trace propagation pattern
        const shouldPropagateTrace = tracePropagationPatterns.some(pattern => pattern.test(url));
        
        if (shouldPropagateTrace) {
          // Enable trace context propagation for matching URLs
          info.propagateTraceContext = true;
        }
        
        // Future: Add header/body redaction here if needed
        // Example: if (info.headers) { delete info.headers['authorization']; }
        // Future: Add custom metadata here if needed
        
        return info;
      },
    });

    console.log('[Bugsnag] Performance monitoring initialized successfully');
    console.log('[Bugsnag Performance] API Key:', `${BUGSNAG_API_KEY.substring(0, 8)}...`);
    console.log('[Bugsnag Performance] Release stage:', releaseStage);
    console.log('[Bugsnag Performance] Enabled release stages:', enabledReleaseStages);
    console.log('[Bugsnag Performance] App version:', appVersion);
    console.log('[Bugsnag Performance] Release stage enabled:', enabledReleaseStages.includes(releaseStage));
    console.log('[Bugsnag Performance] Sampling probability:', `${(samplingProbability * 100).toFixed(0)}% (quota management enabled)`);

    // Create a test span to verify performance monitoring is working
    setTimeout(() => {
      try {
        const testSpan = BugsnagPerformance.startSpan('app-initialization');
        console.log('[Bugsnag Performance] Test span created');
        setTimeout(() => {
          testSpan.end();
          console.log('[Bugsnag Performance] Test span ended - check Bugsnag dashboard for this span');
        }, 100);
      } catch (spanError) {
        console.error('[Bugsnag Performance] Error creating test span:', spanError);
      }
    }, 1000);
  } catch (error) {
    console.error('[Bugsnag Performance] Failed to initialize:', error);
  }
} else {
  console.warn('[Bugsnag Performance] REACT_APP_BUGSNAG_API_KEY not set. Performance monitoring is disabled.');
}

// Get the React plugin and create ErrorBoundary component
const reactPlugin = bugsnagClient.getPlugin('react');
const BugsnagErrorBoundary = reactPlugin 
  ? reactPlugin.createErrorBoundary(React)
  : (({ children }: { children: React.ReactNode }) => <>{children}</>);

// Log Bugsnag ErrorBoundary status
if (reactPlugin) {
  console.log('[Bugsnag] React ErrorBoundary initialized successfully');
} else {
  console.warn('[Bugsnag] React plugin not found - ErrorBoundary fallback used');
}

const container = document.getElementById('root');
if (!container) throw new Error('Failed to find the root element');
const root = createRoot(container);

/**
 * A data router, so pages can hold a navigation while there are unsaved changes (useBlocker,
 * EDIT-05) - including the browser Back and Forward buttons, which BrowserRouter can't. Every
 * route still lives in App's <Routes>, matched under this one catch-all route.
 */
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <AuthProvider>
        <App />
      </AuthProvider>
    )
  }
]);

root.render(
  <React.StrictMode>
    <BugsnagErrorBoundary>
      <RouterProvider router={router} />
    </BugsnagErrorBoundary>
  </React.StrictMode>
);